class MockQueryBuilder {
  private table: string;
  private action: 'select' | 'insert' | 'update' | 'delete' = 'select';
  private selectFields: string = '*';
  private filters: any[] = [];
  private orderObj: any = null;
  private isSingle: boolean = false;
  private limitVal: number | null = null;
  private payload: any = null;

  constructor(table: string) {
    this.table = table;
  }

  select(fields = '*') {
    // Jei prieš tai buvo insert/update/delete — nekeičiame action,
    // tik nustatome selectFields (Supabase .insert().select() = INSERT RETURNING)
    if (this.action === 'select') {
      this.selectFields = fields;
    }
    return this;
  }

  insert(data: any) {
    this.action = 'insert';
    this.payload = data;
    return this;
  }

  update(data: any) {
    this.action = 'update';
    this.payload = data;
    return this;
  }

  delete() {
    this.action = 'delete';
    return this;
  }

  eq(field: string, value: any) {
    this.filters.push({ type: 'eq', field, value });
    return this;
  }

  ilike(field: string, value: any) {
    this.filters.push({ type: 'ilike', field, value });
    return this;
  }

  like(field: string, value: any) {
    this.filters.push({ type: 'like', field, value });
    return this;
  }

  order(field: string, options?: { ascending?: boolean }) {
    this.orderObj = { field, ascending: options?.ascending !== false };
    return this;
  }

  limit(val: number) {
    this.limitVal = val;
    return this;
  }

  single() {
    this.isSingle = true;
    return this;
  }

  async execute() {
    try {
      const res = await fetch('/api/pg', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          table: this.table,
          action: this.action,
          selectFields: this.selectFields,
          filters: this.filters,
          order: this.orderObj,
          single: this.isSingle,
          limit: this.limitVal,
          data: this.payload,
        }),
      });
      return await res.json();
    } catch (err) {
      return { data: null, error: err };
    }
  }

  then(onfulfilled?: (value: any) => any, onrejected?: (reason: any) => any): Promise<any> {
    return this.execute().then(onfulfilled, onrejected);
  }
}

const getStoredSession = () => {
  if (typeof window === 'undefined') return null;
  try {
    const val = localStorage.getItem('pg-auth-session');
    return val ? JSON.parse(val) : null;
  } catch {
    return null;
  }
};

// Deduplicated auto-login promise — only one network call at a time
let pendingAutoLogin: Promise<any> | null = null;

async function getOrCreateSession(): Promise<any> {
  const stored = getStoredSession();
  if (stored) return stored;

  if (!pendingAutoLogin) {
    pendingAutoLogin = fetch('/api/pg/auth', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ action: 'signin', email: 'demo@takeoffai.com', password: 'demo1234' }),
    })
      .then((r) => r.json())
      .then((result) => {
        if (result.data?.session) {
          setStoredSession(result.data.session);
          authListeners.forEach((cb) => cb('SIGNED_IN', result.data.session));
          return result.data.session;
        }
        return null;
      })
      .catch(() => null)
      .finally(() => {
        pendingAutoLogin = null;
      });
  }

  return pendingAutoLogin;
}

const setStoredSession = (session: any) => {
  if (typeof window === 'undefined') return;
  try {
    if (session) {
      // Autoritetinę `pg-auth-token` cookie nustato SERVERIS kaip httpOnly
      // (P3) — čia JS jos NEBERAŠO (XSS apsauga). localStorage lieka tik
      // kliento UI būsenai (ne autentifikacijos autoritetas).
      localStorage.setItem('pg-auth-session', JSON.stringify(session));
    } else {
      localStorage.removeItem('pg-auth-session');
    }
  } catch {}
};

const authListeners = new Set<(event: string, session: any) => void>();

export const mockAuth = {
  async getSession() {
    const session = await getOrCreateSession();
    return { data: { session }, error: null };
  },

  async getUser() {
    const session = await getOrCreateSession();
    return { data: { user: session?.user ?? null }, error: null };
  },

  async signInWithPassword({ email, password }: any) {
    try {
      const res = await fetch('/api/pg/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'signin', email, password }),
      });
      const result = await res.json();
      if (result.data?.session) {
        setStoredSession(result.data.session);
        authListeners.forEach((cb) => cb('SIGNED_IN', result.data.session));
      }
      return result;
    } catch (err: any) {
      return { data: { user: null, session: null }, error: err };
    }
  },

  async signUp({ email, password, options }: any) {
    try {
      const res = await fetch('/api/pg/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'signup', email, password, metadata: options?.data }),
      });
      const result = await res.json();
      if (result.data?.session) {
        setStoredSession(result.data.session);
        authListeners.forEach((cb) => cb('SIGNED_IN', result.data.session));
      }
      return result;
    } catch (err: any) {
      return { data: { user: null, session: null }, error: err };
    }
  },

  async signOut() {
    // Serveris išvalo httpOnly cookie; klientas — localStorage.
    try {
      await fetch('/api/pg/auth', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ action: 'signout' }),
      });
    } catch {}
    setStoredSession(null);
    authListeners.forEach((cb) => cb('SIGNED_OUT', null));
    return { error: null };
  },

  async exchangeCodeForSession(code: string) {
    return { data: { session: null }, error: null };
  },

  onAuthStateChange(callback: (event: any, session: any) => void) {
    authListeners.add(callback);
    const stored = getStoredSession();
    if (stored) {
      callback('INITIAL_SESSION', stored);
    } else {
      // Don't fire SIGNED_OUT — auto-login instead (loading stays true until resolved)
      getOrCreateSession().then((session) => {
        callback(session ? 'SIGNED_IN' : 'SIGNED_OUT', session);
      });
    }
    return {
      data: {
        subscription: {
          unsubscribe() {
            authListeners.delete(callback);
          },
        },
      },
    };
  },
};

export function createClient() {
  return {
    from(table: string) {
      return new MockQueryBuilder(table);
    },
    auth: mockAuth,
  };
}
